import { jsx, jsxs } from "react/jsx-runtime";
import { createContext, useState } from "react";
import { useAmplitude } from "@crehana/analytics";
import { useMe } from "@crehana/auth";
import { useTranslation } from "@crehana/i18n";
import { Snackbar } from "@crehana/ui";
import { AMPLITUDE_ACTION_EVENT_NAMES, SAVED_COURSES_TRANSLATION_KEY } from "./constants.js";
import useDeleteCourse from "./hooks/useDeleteCourse/index.js";
import useSavedCourse from "./hooks/useSavedCourse/index.js";
import i18n from "./i18n/index.js";
const SavedCoursesContext = /*#__PURE__*/ createContext(void 0);
const SavedCoursesProvider = ({ isDark = false, children })=>{
    const { logEvent } = useAmplitude();
    const [loading, setLoading] = useState(false);
    const [snackbarData, setSnackbarData] = useState({
        open: false,
        message: '',
        type: 'success'
    });
    const { t, addResourceBundle } = useTranslation(SAVED_COURSES_TRANSLATION_KEY);
    const { saveCourse: saveCourseHook } = useSavedCourse();
    const { deleteCourse: deleteCourseHook } = useDeleteCourse();
    addResourceBundle(i18n);
    const { me } = useMe();
    const getCommonAmplitudeData = (args)=>({
            'Course ID': args.courseId,
            'Course Name': args.courseName,
            Category: args.category,
            Subcategory: args.subcategory,
            Source: args.source,
            'Source Detail': args.sourceDetail
        });
    const openSnackbar = (msg, type = 'success')=>{
        setSnackbarData({
            message: msg,
            open: true,
            type
        });
    };
    const handleErrorResponse = (reason, actionType)=>{
        openSnackbar('save' === actionType ? t('SC_ERROR_SAVE_COURSE') : t('SC_ERROR_DELETE_COURSE'), 'error');
        throw new Error(reason);
    };
    const closeSnackbar = ()=>setSnackbarData((prev)=>({
                ...prev,
                open: false
            }));
    const isEnabledSaveCourses = !!me?.isPremium;
    const saveCourse = async (courseId, args)=>{
        const _args = {
            launchAmplitudeEvent: true,
            showSnackbar: true,
            ...args
        };
        setLoading(true);
        const promiseResult = saveCourseHook(courseId);
        try {
            const response = await promiseResult;
            if (_args.showSnackbar) openSnackbar(t('SC_BOOKMARK_ADDED_COURSE'));
            if (_args.launchAmplitudeEvent) logEvent({
                event: AMPLITUDE_ACTION_EVENT_NAMES.COURSE_SAVED,
                data: getCommonAmplitudeData({
                    courseId,
                    ..._args.amplitudeData
                })
            });
            return response;
        } catch (reason) {
            return handleErrorResponse(reason, 'save');
        } finally{
            setLoading(false);
        }
    };
    const deleteCourse = async (courseId, args)=>{
        const _args = {
            launchAmplitudeEvent: true,
            showSnackbar: true,
            ...args
        };
        setLoading(true);
        const promiseResult = deleteCourseHook(courseId);
        try {
            const response = await promiseResult;
            if (_args.showSnackbar) openSnackbar(t('SC_BOOKMARK_REMOVED_COURSE'));
            if (_args.launchAmplitudeEvent) logEvent({
                event: AMPLITUDE_ACTION_EVENT_NAMES.REMOVE_COURSE,
                data: getCommonAmplitudeData({
                    courseId,
                    ..._args.amplitudeData
                })
            });
            return response;
        } catch (reason) {
            return handleErrorResponse(reason, 'delete');
        } finally{
            setLoading(false);
        }
    };
    const toggleBookmarkSelection = (isSavedCourse, courseId, args)=>isSavedCourse ? deleteCourse(courseId, args) : saveCourse(courseId, args);
    const providerValue = {
        saveCourse,
        deleteCourse,
        toggleBookmarkSelection,
        isEnabledSaveCourses,
        loading
    };
    const snackbar = 'test' === process.env.NODE_ENV ? void 0 : /*#__PURE__*/ jsx(Snackbar, {
        open: snackbarData.open,
        position: "top-right",
        state: 'error' === snackbarData.type ? 'alert' : 'success',
        dismissOnDelay: 3000,
        onDismiss: closeSnackbar,
        isDark: isDark,
        message: snackbarData.message,
        dismissOnClickOutside: true
    });
    return /*#__PURE__*/ jsxs(SavedCoursesContext.Provider, {
        value: providerValue,
        children: [
            children,
            snackbar
        ]
    });
};
const SavedCoursesContext_SavedCoursesContext = SavedCoursesProvider;
export default SavedCoursesContext_SavedCoursesContext;
export { SavedCoursesContext };
